/* CUSTOM:paypal — PayPal Pay Later line in the cart page and the Rebuy Smart Cart.

   The line asks the PayPal Payments Studio app for its text, the same server the app block on the
   product page asks (`/apps/paypal/messaging`), with the cart's own currency and the buyer's country.
   So the cart follows the market exactly like the product page:
   - US, USD 120   -> "Pay in full or 4 interest-free payments from $30.00 with PayPal Pay in 4…"
   - GB, GBP 120   -> "… 3 interest-free payments from £40.00 with PayPal Pay in 3…"
   - BR (USD), VN, JP, HK… -> the app has no Pay Later there, so no line.

   It replaces the PayPal Messages SDK v5 the theme used to load. That SDK was loaded in USD with the
   country hard-coded to US, so a GBP 100 cart read "4 payments of $25.00" and a VND cart was taken as
   dollars. Audit: shopify-themes-hub knowledge/paypal-countries.md.

   The app's endpoint is not documented. Any error or unexpected shape hides the line, never shows a
   wrong one.

   "Learn more" opens PayPal's Learn More modal, the same one the product page block opens. It uses
   the SDK instance the app embed `paypal-sdk` creates on every page (`window.paypalSdkInstance`), the
   way the app's own paypal-messaging.js does. Not a documented API for themes: when the instance is
   missing or the call fails, the link opens PayPal's Pay Later page in a new tab instead.

   Placeholders: any element with `data-pp-message` and `data-pp-amount` (cart total, major units).
   Optional `data-pp-style-text-color` and `data-pp-style-text-size`. Config comes from
   layout/theme.liquid: `window.theme.paypalCart = { currency, locale, country, currencies }`.
   Call `theme.loadPayPalMessages(target)` after inserting a placeholder (the Rebuy drawer does). On the
   cart page the section is re-rendered after each quantity change; a MutationObserver on <main> picks
   that up, and only on pages that start with a placeholder. */
(function () {
  'use strict';

  window.theme = window.theme || {};

  const ENDPOINT = '/apps/paypal/messaging';
  // PayPal's own Pay Later page per country; the app only returns a message for these.
  const LEARN_MORE_PATHS = { US: 'us', GB: 'uk', AU: 'au', CA: 'ca', FR: 'fr', DE: 'de', IT: 'it', ES: 'es' };
  const responses = new Map();

  function config() {
    return window.theme.paypalCart || null;
  }

  function fetchText(price, cfg) {
    const key = `${cfg.currency}|${cfg.locale}|${price}`;
    if (responses.has(key)) return responses.get(key);

    const params = new URLSearchParams({ surface: 'cart', price, currency: cfg.currency, locale: cfg.locale });
    const request = fetch(`${ENDPOINT}?${params}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const bnpl = (data && data.bnplMessaging) || {};
        const pick = bnpl.compound || bnpl.payIn4?.message || bnpl.payIn3?.message || bnpl.payMonthly?.message || bnpl.payIn30?.message;
        return typeof pick === 'string' && pick.trim() ? { text: pick, offer: offerOf(bnpl) } : null;
      })
      .catch(() => {
        responses.delete(key); // a network blip must not hide the line for the rest of the visit
        return null;
      });

    responses.set(key, request);
    return request;
  }

  // Which tab the modal opens on (US only), same rule as the app: Pay in 4 wins over Pay Monthly.
  function offerOf(bnpl) {
    if (bnpl.payIn4?.message || bnpl.payIn30?.message) return 'PAY_LATER_SHORT_TERM';
    if (bnpl.payMonthly?.message) return 'PAY_LATER_LONG_TERM';
    return '';
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // The app's markup: "{{PayPal}}" is the logo, "{$30.00}" the amount, "[[Learn more]]" the link.
  function toHtml(text, cfg) {
    const learnMore = `https://www.paypal.com/${LEARN_MORE_PATHS[cfg.country] || 'us'}/digital-wallet/ways-to-pay/buy-now-pay-later`;
    return escapeHtml(text)
      .replace(/\{\{\s*PayPal\+?\s*\}\}/g, '<span class="paypal-cart-message__logo">PayPal</span>')
      .replace(/\{([^{}]+)\}/g, '<strong>$1</strong>')
      .replace(/\[\[([^\]]+)\]\]/g, `<a href="${learnMore}" target="_blank" rel="noopener" data-pp-cart-learn-more>$1</a>`);
  }

  function hide(node) {
    node.innerHTML = '';
    node.hidden = true;
  }

  function priceOf(node) {
    const amount = Number(node.dataset.ppAmount);
    return amount > 0 ? amount.toFixed(2) : null;
  }

  function render(node) {
    const cfg = config();
    const price = priceOf(node);
    node.dataset.ppRendered = price || 'none';
    if (!cfg || !price || !(cfg.currencies || []).includes(cfg.currency)) {
      hide(node);
      return Promise.resolve(false);
    }

    return fetchText(price, cfg).then((message) => {
      if (node.dataset.ppRendered !== price) return false; // a newer total took over this node
      if (!message) {
        hide(node);
        return false;
      }
      const color = node.dataset.ppStyleTextColor;
      const size = Number(node.dataset.ppStyleTextSize);
      const style = `${color ? `color:${escapeHtml(color)};` : ''}${size ? `font-size:${size}px;` : ''}`;
      node.dataset.ppOffer = message.offer;
      node.innerHTML = `<p class="paypal-cart-message"${style ? ` style="${style}"` : ''}>${toHtml(message.text, cfg)}</p>`;
      node.hidden = false;
      return true;
    });
  }

  function placeholders(target) {
    const root = typeof target === 'string' ? document.querySelector(target) : target || document;
    if (!root) return [];
    const nodes = Array.from(root.querySelectorAll ? root.querySelectorAll('[data-pp-message]') : []);
    if (root.matches && root.matches('[data-pp-message]')) nodes.unshift(root);
    return nodes;
  }

  theme.loadPayPalMessages = function loadPayPalMessages(target = document) {
    const nodes = placeholders(target).filter((node) => node.dataset.ppRendered !== (priceOf(node) || 'none'));
    return Promise.all(nodes.map(render)).then((results) => results.some(Boolean));
  };

  // The SDK writes the buyer country into the modal iframe as `country=`, but PayPal's page reads
  // `buyer_country=`. The app's paypal-messaging.js patches the same SDK bug the same way.
  function correctedModalSrc(src, offer) {
    if (!src || !src.includes('paypal.com')) return null;
    let url;
    try {
      url = new URL(src);
    } catch (error) {
      return null;
    }
    let changed = false;
    const country = url.searchParams.get('country');
    if (country && !url.searchParams.has('buyer_country')) {
      url.searchParams.set('buyer_country', country);
      url.searchParams.delete('country');
      changed = true;
    }
    if (offer && !url.searchParams.has('offer') && url.searchParams.get('buyer_country')?.toUpperCase() === 'US') {
      url.searchParams.set('offer', offer);
      changed = true;
    }
    return changed ? url.toString() : null;
  }

  // The v6 modal is a <dialog> whose ::backdrop is rgba(0,0,0,.8). The v5 modal the cart opened before
  // (still on main) had a lighter grey overlay: white read rgb(121,126,130) under it. Same look here.
  const BACKDROP_CSS = 'dialog::backdrop{background:rgba(97,103,108,.85)!important}';

  // The modal iframe and <dialog> sit in nested shadow roots under <paypal-sdk-iframe-modal>.
  function patchModal(session) {
    if (session.closed) return;
    let open = false;
    const visit = (root) => {
      if (!session.watched.has(root)) {
        session.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'open'] });
        session.watched.add(root);
      }
      if (root.querySelector('dialog') && !root.querySelector('style[data-pp-backdrop]')) {
        const style = document.createElement('style');
        style.dataset.ppBackdrop = '';
        style.textContent = BACKDROP_CSS;
        root.appendChild(style);
        session.styles.push(style);
      }
      if (root.querySelector('dialog[open]')) open = true;
      root.querySelectorAll('iframe').forEach((iframe) => {
        const src = correctedModalSrc(iframe.getAttribute('src'), session.offer);
        if (src) iframe.setAttribute('src', src);
      });
      root.querySelectorAll('*').forEach((el) => el.shadowRoot && visit(el.shadowRoot));
    };
    document.querySelectorAll('paypal-sdk-iframe-modal').forEach((el) => el.shadowRoot && visit(el.shadowRoot));
    // The SDK calls onClose for its X button but not for Esc, so also end the session once the
    // dialog it opened has closed. Old dialogs from earlier openings stay in the DOM, closed.
    if (open) session.seenOpen = true;
    else if (session.seenOpen) session.close();
  }

  function openLearnMoreModal(node, cfg) {
    const instance = window.paypalSdkInstance;
    if (!instance || typeof instance.createPayPalMessages !== 'function') return false;

    const urlBefore = window.location.href;
    const session = {
      offer: cfg.country === 'US' ? node.dataset.ppOffer : '',
      watched: new WeakSet(),
      styles: [], // removed on close, so the product page block's modal keeps PayPal's own look
      closed: false,
      close() {
        if (session.closed) return;
        session.closed = true;
        session.observer.disconnect();
        session.styles.forEach((style) => style.remove());
        // The modal pushes a history entry; drop it so Back after checkout does not reopen it.
        if (window.location.href !== urlBefore) window.history.replaceState(window.history.state, '', urlBefore);
      },
    };
    session.observer = new MutationObserver(() => patchModal(session));
    session.observer.observe(document.body, { childList: true, subtree: true });

    try {
      const learnMore = instance.createPayPalMessages().createLearnMore({
        amount: priceOf(node),
        currencyCode: cfg.currency,
        buyerCountry: cfg.country,
        presentationMode: 'MODAL',
        onShow: () => patchModal(session),
        onClose: session.close,
      });
      if (!learnMore) throw new Error('createLearnMore returned nothing');
      Promise.resolve(learnMore.open()).catch((error) => console.warn('[PayPal] Learn more modal failed to open:', error));
      return true;
    } catch (error) {
      session.close();
      console.warn('[PayPal] Learn more modal unavailable, opening the page instead:', error);
      return false;
    }
  }

  // Capture phase: the Rebuy drawer may stop clicks from bubbling.
  document.addEventListener('click', (event) => {
    // Not `data-pp-learn-more`: the SDK puts that attribute on its own <paypal-sdk-iframe-modal>.
    const link = event.target.closest?.('a[data-pp-cart-learn-more]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const node = link.closest('[data-pp-message]');
    const cfg = config();
    if (!node || !cfg || !window.paypalSdkInstance) return; // the link opens the page in a new tab
    event.preventDefault();
    if (!openLearnMoreModal(node, cfg)) window.open(link.href, '_blank', 'noopener');
  }, true);

  function injectStyles() {
    if (document.getElementById('paypal-cart-message-style')) return;
    const style = document.createElement('style');
    style.id = 'paypal-cart-message-style';
    style.textContent =
      '.paypal-cart-message{margin:0;font-size:12px;line-height:1.5}' +
      '.paypal-cart-message a{color:inherit;text-decoration:underline}' +
      '.paypal-cart-message__logo{font-weight:700;font-style:italic}';
    document.head.appendChild(style);
  }

  function start() {
    injectStyles();
    theme.loadPayPalMessages(document);

    const main = document.getElementById('MainContent');
    if (!main || !main.querySelector('[data-pp-message]')) return;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        theme.loadPayPalMessages(main);
      });
    }).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
